import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  classifyAccounts,
  getAccountSettings,
  getDepartmentSettings,
  queryKeys,
  saveDepartmentSettings,
  setTileMode,
} from '@/api/fpa'
import type { AccountClassification, DepartmentSettings, TileInput, TileMode } from '@/api/types'

/** Income-statement accounts with their classification and the quarter's posted amount. */
export function useAccountSettings(quarter: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.accountSettings(quarter),
    queryFn: ({ signal }) => getAccountSettings(quarter, signal),
    enabled,
  })
}

/** Classify accounts (admin). OpEx totals change, so every runway and drill-down is refreshed. */
export function useClassifyAccounts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accounts, classification }: { accounts: string[]; classification: AccountClassification }) =>
      classifyAccounts(accounts, classification),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.accountSettingsAll }),
        queryClient.invalidateQueries({ queryKey: queryKeys.runwayAll }),
        queryClient.invalidateQueries({ queryKey: queryKeys.transactionsAll }),
      ])
    },
  })
}

export function useDepartmentSettings(enabled = true) {
  return useQuery({
    queryKey: queryKeys.departmentSettings,
    queryFn: ({ signal }) => getDepartmentSettings(signal),
    enabled,
  })
}

/** Replace the whole tile set (admin, manager). */
export function useSaveDepartmentSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tiles: TileInput[]) => saveDepartmentSettings(tiles),
    onSuccess: (saved: DepartmentSettings) => queryClient.setQueryData(queryKeys.departmentSettings, saved),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.runwayAll }),
        queryClient.invalidateQueries({ queryKey: queryKeys.transactionsAll }),
      ])
    },
  })
}

/** Which tiles the dashboard shows (admin, manager). Every runway and the settings are refreshed. */
export function useSetTileMode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (mode: TileMode) => setTileMode(mode),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.runwayAll }),
        queryClient.invalidateQueries({ queryKey: queryKeys.departmentSettings }),
      ])
    },
  })
}
